import {goingForwardPropertyIdSelector} from '@stencil/hotels-common-reducer/src/selectors';

import {
  updateDetailsStaticFetchStatus,
  updateDetailsStaticData,
  updateDetailsHermesFetchStatus,
  updateDetailsHermesData,
  updateDetailsPageConfigAction,
  updateSafetyHygieneMediaData,
  // hotel policy
  updateHotelPolicyList,
  updateHotelPolicyAdditionalData,
  updateHotelPolicyFetchStatus,
  updateHotelPolicyError,

  // similar hotels
  updateSimilarHotels,
  updateSimilarHotelsFetchStatus,
  updateStaticDataFetchStatus,
} from './actions';

import {AppState} from './../../../store';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {parseDateIntoHermesString} from '@stencil/hotels-utils/src/date-utils/date-utils';
import parseDetailsPageUrlToConfig from '@stencil/hotels-utils/src/parse-details-page-url-to-config';

import {updateQueryAction} from '@stencil/hotels-common-reducer/src/actions';
import {IUpdate_Query} from '@stencil/hotels-common-reducer/src/types';

import fetchHotelsDetailsStaticData from '@stencil/hotels-details-static-api';
import fetchDetailsHermesData from '@stencil/hotels-details-hermes-api';
import fetchHotelPolicy from '@stencil/hotels-details-policy-api';
import fetchVoyagerMediaImages from '@stencil/hotels-get-voyager-media-images';
import {IUpdate_Details_Config_Action, FetchStatusType, DetailsHermesShape} from './types';

import {hotelPolicyThunkSelector, similarHotelsThunkSelector} from './selectors';

import fetchSimilarHotels from '@stencil/hotels-details-similar-hotels-api';

export const fetchDetailsStaticDataThunk = (
  flavour = 'mobile',
): ThunkAction<void, AppState, {}, AnyAction> => {
  // Invoke API
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    // getState()
    // 🐨 change Array<any> to Array<{typeof staticData}>
    dispatch(updateDetailsStaticFetchStatus({isLoading: true}));
    dispatch(updateStaticDataFetchStatus(FetchStatusType.PENDING));
    const state = getState();
    const {details, common} = state;
    const {destination} = common;
    const {filter} = details;

    const vhid = goingForwardPropertyIdSelector(state);

    return fetchHotelsDetailsStaticData({
      vhid,
      destination,
      filter,
      flavour,
    })
      .then(response => {
        if (response.data && Array.isArray(response.data) && response.data[0]) {
          dispatch(updateDetailsStaticData(response?.data[0]));
          if (!destination?._id || !destination?.n) {
            // filling destination on client side, required for dweb.
            dispatch(
              updateQueryAction({
                destination: {
                  _id: response?.data[0]?.vcid || '',
                  n: response?.data[0]?.c || '',
                  t: 'city',
                },
              }),
            );
          }
        }

        dispatch(updateDetailsStaticFetchStatus({isLoading: false}));
        dispatch(updateStaticDataFetchStatus(FetchStatusType.RESOLVED));
      })
      .catch(err => {
        console.trace(err);
        dispatch(updateStaticDataFetchStatus(FetchStatusType.REJECTED));
        // dispatch(updateHasDataAction(false));
      });
  };
};

export const fetchDetailsHermesDataThunk = ({
  hermesSuccessCallback: successCallbackForPerformanceMarking = () => {},
  apiVersion = 'v12',
  flavor,
}): // hermesUrl: string,
ThunkAction<void, AppState, {}, AnyAction> => {
  // Invoke API
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    // getState()
    // 🐨 change Array<any> to Array<{typeof staticData}>

    dispatch(updateDetailsHermesFetchStatus({isLoading: true}));

    const reduxState = getState();

    const {common, details} = reduxState;
    const {checkin, checkout, roomString} = common;
    const {selectedHotelStatic, filter, staticData, offerCodeFromQueryParam} = details;

    const vhid = goingForwardPropertyIdSelector(reduxState);

    const dataToMakeHermesUrl = {
      vhid,
      cityId: staticData.vcid,
      checkin,
      checkout,
      roomString,
      staticData,
      selectedHotelStatic,
      filter,
      offerCodeFromQueryParam,
      apiVersion,
      flavor,
    };

    return fetchDetailsHermesData(dataToMakeHermesUrl)
      .then(response => {
        dispatch(updateDetailsHermesData(response as DetailsHermesShape));
        dispatch(updateDetailsHermesFetchStatus({isLoading: false}));
        successCallbackForPerformanceMarking();
      })
      .catch(err => {
        console.trace(err);
        // dispatch(updateHasDataAction(false));
      });
  };
};

export const initializeDetailsPageThunk = (
  searchString,
  params,
): ThunkAction<void, AppState, {}, AnyAction> => {
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const parsedConfig = parseDetailsPageUrlToConfig(searchString, params);

    dispatch(
      updateQueryAction({
        destination: {
          _id: parsedConfig.selectedHotelStatic.hc,
          n: parsedConfig.selectedHotelStatic.hn,
        },
        checkin: parsedConfig.checkin,
        checkout: parsedConfig.checkout,
        roomString: parsedConfig.roomString,
      } as IUpdate_Query['actionData']),
    );

    dispatch(
      updateDetailsPageConfigAction(parsedConfig as IUpdate_Details_Config_Action['actionData']),
    );
  };
};

export const fetchHotelPolicyDataThunk = (): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const reduxState = getState();

    let {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
      roomsDataHermes,
      oldPax,
      hotelPolicyFromHermesData,
    } = hotelPolicyThunkSelector(reduxState);

    const {showHotelPolicyFromHermes, hotelPolicyListFromHermes} = hotelPolicyFromHermesData;

    const showPolicyFromHermes =
      showHotelPolicyFromHermes &&
      hotelPolicyListFromHermes &&
      Array.isArray(hotelPolicyListFromHermes) &&
      hotelPolicyListFromHermes.length;

    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    if (paxSplittedRoomString) {
      roomstring = paxSplittedRoomString;
    }

    if (oldPax) {
      roomstring = oldPax;
    }

    const firstRoom = roomsDataHermes[0] || ({} as typeof roomsDataHermes[0]);

    const {rpl: flavorsOfFirstRoom = []} = firstRoom;
    const firstFavorOfFirstRoom = flavorsOfFirstRoom[0] || ({} as typeof flavorsOfFirstRoom[0]);
    const {fwdp = {}, ibp} = firstFavorOfFirstRoom;

    dispatch(updateHotelPolicyFetchStatus(FetchStatusType.PENDING));

    const [hotelPolicyData, hotelPolicyError] = await fetchHotelPolicy({
      checkin,
      checkout,
      pax: roomstring,
      cityId,
      hotelCode,
      forwardParams: fwdp,
      vendor: ibp,
    });

    if (hotelPolicyData.hotelPolicyList) {
      dispatch(updateHotelPolicyFetchStatus(FetchStatusType.RESOLVED));

      /**
       * if hotel policy has *NOT* to be shown from hermes, update hotel policy list
       * with the freshly fetched list
       */

      if (!showPolicyFromHermes) {
        dispatch(updateHotelPolicyList(hotelPolicyData.hotelPolicyList));
      }
    }

    /**
     * if hotel policy has to be shown from hermes
     */
    if (showPolicyFromHermes) {
      dispatch(updateHotelPolicyFetchStatus(FetchStatusType.RESOLVED));
      dispatch(updateHotelPolicyList(hotelPolicyListFromHermes));
    }

    if (hotelPolicyData.additionalData) {
      dispatch(updateHotelPolicyAdditionalData(hotelPolicyData.additionalData));
    }

    if (hotelPolicyError) {
      dispatch(updateHotelPolicyError(hotelPolicyError));
    }
  };
};

export const fetchSimilarHotelsThunk = (
  flavor: 'mobile' | 'v3',
): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const reduxState = getState();

    let {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
    } = similarHotelsThunkSelector(reduxState);

    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    if (paxSplittedRoomString) {
      roomstring = paxSplittedRoomString;
    }

    dispatch(updateSimilarHotelsFetchStatus(FetchStatusType.PENDING));

    const [similarHotelsData, error] = await fetchSimilarHotels({
      checkinYYYYMMDD: checkin,
      checkoutYYYYMMDD: checkout,
      pax: roomstring,
      cityId,
      hotelCode,
      flavor,
    });

    if (similarHotelsData) {
      dispatch(
        updateSimilarHotels({
          list: similarHotelsData.data || [],
          cityMetaInfo: similarHotelsData.city_meta_info,
        }),
      );
    }

    if (error) {
      dispatch(updateSimilarHotelsFetchStatus(FetchStatusType.REJECTED));
    }
  };
};

export const fetchMediaImages = (tagId): ThunkAction<void, AppState, {}, AnyAction> => {
  // Invoke API
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {details} = getState();
    const {vhid} = details;

    return fetchVoyagerMediaImages({
      vhid,
      tagId,
    })
      .then(response => {
        if (response?.success && response?.data && response?.data[tagId])
          dispatch(updateSafetyHygieneMediaData(response?.data[tagId]));
      })
      .catch(err => {
        console.trace(err);
        dispatch(updateSafetyHygieneMediaData([]));

        // dispatch(updateHasDataAction(false));
      });
  };
};
